import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Leaf, Users, Truck, MapPin, ArrowRight, Heart, Percent,
  Coins, Sprout, Clock, PackageCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { aboutAPI } from '@/lib/api';

const STEP_ICONS = [Users, Truck, MapPin];
const FEE_ICONS = [Percent, Percent, Truck, Coins];

// Resilient fallback so the page renders instantly / even if the API is down.
const FALLBACK = {
  hero_eyebrow: 'Community-Driven Buying Group · Western Cape',
  hero_title: 'About',
  hero_title_accent: 'AVZ Wholesale',
  hero_welcome:
    'Hi, I am Antoinette, welcome to AVZ Wholesale! We are a community-driven buying group dedicated to making healthy, premium, and wholesome products affordable and accessible to families across the Western Cape.',
  story_eyebrow: 'Our Story',
  story_heading: 'A family-run idea, grown by a community.',
  story_body:
    'AVZ Wholesale started as a passionate side hustle while navigating the beautiful, busy life of homeschooling my four-year-old with a baby tagging along. It grew out of a simple, genuine desire: to help everyday people buy high-quality, healthy products for significantly less than retail shop prices.\n\nBy pooling our orders together, we bypass the heavy retail markups and bring the savings directly to your pantry.',
  story_quote:
    "When you support AVZ Wholesale, you aren't just buying groceries — you're supporting a local, family-run initiative and a community of like-minded households.",
  how_eyebrow: 'How It Works',
  how_heading: 'A group-buying consolidation model',
  how_intro:
    "To keep prices as low as possible, we don't warehouse or hold excess stock. Instead, we compile everyone's orders into one bulk shipment, straight from our trusted suppliers.",
  how_steps: [
    { title: 'Pool Orders Together', body: "We gather everyone's orders into one combined buy — so the whole community shares in wholesale pricing." },
    { title: 'One Bulk Shipment', body: 'We compile orders into a single bulk order straight from our trusted suppliers. No warehousing, no excess stock, no heavy retail markup.' },
    { title: 'Collect Locally', body: 'Once shipments arrive we sort and pack each order. Collect from a convenient regional drop-off point when it is ready.' },
  ],
  fees_eyebrow: 'Transparent Pricing & Fees',
  fees_heading: 'No surprises, ever',
  fees_intro:
    'To cover the time, logistics, and banking costs of managing these bulk orders, we add a small, clearly-stated admin fee to the wholesale prices.',
  fees_cards: [
    { value: '8%', label: 'Standard Admin Fee', note: 'Added to your order total.' },
    { value: '10%', label: 'Lowerland Orders', note: 'Admin fee on Lowerland orders.' },
    { value: 'R15', label: 'Transport Fee', note: 'Covers fuel to distribute orders to collection points.' },
    { value: '%', label: 'Courier Charges', note: 'For suppliers that charge delivery, shipping is a fair percentage of the subtotal.' },
  ],
  collection_eyebrow: 'Collection Points',
  collection_heading: 'Pick up close to home',
  collection_intro:
    "Once our bulk shipments arrive, every order is unpacked, sorted, and packed for you. Collect from one of our convenient regional drop-off points once it's ready.",
  collection_note:
    'To support our wonderful hosts, kindly arrange to collect your items as soon as possible — ideally within 48 hours for business premises. Full collection addresses are shared with you once your order is ready.',
  collection_points: [
    { location: 'Aan de Wijnlanden', fee: 'Free' },
    { location: 'Stellenbosch', fee: 'R10' },
    { location: 'Strand', fee: 'R15' },
    { location: 'Somerset West', fee: 'R15' },
    { location: 'Hermanus', fee: 'R10' },
  ],
  cta_heading: 'Thank you for being part of our community',
  cta_body:
    "You're helping us make healthy living more affordable for everyone. Have a question about joining a supplier group or placing your first order? We'd love to hear from you.",
};

export default function AboutPage() {
  const { user } = useAuth();
  const [c, setC] = useState(FALLBACK);

  useEffect(() => {
    aboutAPI.get().then(res => res.data && setC({ ...FALLBACK, ...res.data })).catch(() => {});
  }, []);

  const storyParas = (c.story_body || '').split(/\n\n+/).filter(Boolean);

  return (
    <div data-testid="about-page" className="bg-background">
      {/* ===== HERO ===== */}
      <section
        data-testid="about-hero"
        className="relative min-h-[52vh] sm:min-h-[60vh] lg:min-h-[70vh] flex items-center overflow-hidden bg-charcoal"
      >
        <div className="absolute inset-0 z-0">
          <img
            src="/about-hero.png"
            alt="Wholesome natural pantry products"
            className="w-full h-full object-cover object-center"
            onError={(e) => { e.currentTarget.src = '/hero-image.png'; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/55 to-charcoal/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 w-full">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-5 animate-fade-up">
              <span className="h-px w-8 bg-gold" />
              <p className="font-body text-xs sm:text-sm uppercase tracking-[0.3em] text-gold font-bold">
                {c.hero_eyebrow}
              </p>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-normal text-white tracking-tight leading-[1.05] mb-6 animate-fade-up drop-shadow-lg">
              {c.hero_title}
              <span className="block italic text-gold-light mt-1">{c.hero_title_accent}</span>
            </h1>

            <p className="font-body text-base sm:text-lg text-white/85 leading-relaxed max-w-xl mb-2 animate-fade-up stagger-2">
              {c.hero_welcome}
            </p>
          </div>
        </div>
      </section>

      {/* ===== OUR STORY ===== */}
      <section data-testid="about-story" className="py-14 sm:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className="font-body text-xs sm:text-sm uppercase tracking-[0.3em] text-teal font-bold mb-3">
                {c.story_eyebrow}
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-foreground tracking-tight mb-6 leading-tight">
                {c.story_heading}
              </h2>
              <div className="space-y-4 font-body text-sm sm:text-base text-foreground/70 leading-relaxed">
                {storyParas.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-teal/15 to-gold/10 blur-xl" />
              <div className="relative rounded-3xl bg-card border border-border p-8 sm:p-10 shadow-xl">
                <Sprout className="h-9 w-9 text-gold mb-5" strokeWidth={1.5} />
                <p className="font-heading text-xl sm:text-2xl font-light text-foreground leading-snug italic">
                  “{c.story_quote}”
                </p>
                <div className="flex items-center gap-2 mt-6 text-teal">
                  <Leaf className="h-4 w-4" />
                  <span className="font-body text-xs uppercase tracking-widest font-semibold">
                    Healthy living, made affordable
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section data-testid="about-how-it-works" className="py-14 sm:py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
            <p className="font-body text-xs sm:text-sm uppercase tracking-[0.3em] text-teal font-bold mb-3">
              {c.how_eyebrow}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-foreground tracking-tight mb-4">
              {c.how_heading}
            </h2>
            <p className="font-body text-sm sm:text-base text-foreground/60 leading-relaxed">
              {c.how_intro}
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-5 sm:gap-7">
            {(c.how_steps || []).map((step, i) => {
              const Icon = STEP_ICONS[i] || PackageCheck;
              return (
                <div
                  key={i}
                  data-testid={`about-step-${i}`}
                  className="relative rounded-2xl bg-card border border-border p-7 sm:p-8 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <span className="absolute top-6 right-7 font-heading text-5xl font-light text-teal/10 select-none">
                    {i + 1}
                  </span>
                  <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-teal/10 text-teal mb-5">
                    <Icon className="h-7 w-7" strokeWidth={1.75} />
                  </div>
                  <h3 className="font-heading text-xl font-medium text-foreground mb-2.5">{step.title}</h3>
                  <p className="font-body text-sm text-foreground/65 leading-relaxed">{step.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== PRICING & FEES (stat cards) ===== */}
      <section data-testid="about-fees" className="py-14 sm:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
            <p className="font-body text-xs sm:text-sm uppercase tracking-[0.3em] text-gold font-bold mb-3">
              {c.fees_eyebrow}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-foreground tracking-tight mb-4">
              {c.fees_heading}
            </h2>
            <p className="font-body text-sm sm:text-base text-foreground/60 leading-relaxed">
              {c.fees_intro}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {(c.fees_cards || []).map((fee, i) => {
              const Icon = FEE_ICONS[i] || Coins;
              return (
                <div
                  key={i}
                  data-testid={`about-fee-${i}`}
                  className="rounded-2xl bg-card border border-border p-6 sm:p-7 text-center hover:border-gold/40 transition-colors duration-300"
                >
                  <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-gold/10 text-gold mb-4">
                    <Icon className="h-6 w-6" strokeWidth={1.75} />
                  </div>
                  <p className="font-heading text-3xl sm:text-4xl font-light text-foreground mb-1">{fee.value}</p>
                  <p className="font-body text-sm font-semibold text-foreground/90 mb-1.5">{fee.label}</p>
                  <p className="font-body text-xs text-foreground/55 leading-relaxed">{fee.note}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== COLLECTION POINTS (privacy: location + fee only) ===== */}
      <section data-testid="about-collection-points" className="py-14 sm:py-24 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10 sm:mb-12">
            <p className="font-body text-xs sm:text-sm uppercase tracking-[0.3em] text-teal font-bold mb-3">
              {c.collection_eyebrow}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-foreground tracking-tight mb-4">
              {c.collection_heading}
            </h2>
            <p className="font-body text-sm sm:text-base text-foreground/60 leading-relaxed">
              {c.collection_intro}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="collection-points-grid">
            {(c.collection_points || []).map((cp, i) => (
              <div
                key={i}
                data-testid={`collection-point-${i}`}
                className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 hover:border-teal/40 transition-colors"
              >
                <span className="inline-flex items-center gap-2.5 font-heading text-lg font-medium text-foreground">
                  <MapPin className="h-5 w-5 text-teal flex-shrink-0" /> {cp.location}
                </span>
                <span className={`font-body text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap ${cp.fee === 'Free' ? 'bg-teal/15 text-teal' : 'bg-gold/15 text-gold'}`}>
                  {cp.fee}
                </span>
              </div>
            ))}
          </div>

          <div className="flex items-start gap-3 mt-6 rounded-xl bg-gold/5 border border-gold/20 p-4">
            <Clock className="h-5 w-5 text-gold flex-shrink-0 mt-0.5" />
            <p className="font-body text-sm text-foreground/70 leading-relaxed">
              <span className="font-semibold text-foreground/90">Please note:</span> {c.collection_note}
            </p>
          </div>
        </div>
      </section>

      {/* ===== CLOSING CTA ===== */}
      <section data-testid="about-cta" className="py-16 sm:py-24 bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="h-9 w-9 text-gold mx-auto mb-5" strokeWidth={1.5} />
          <h2 className="font-heading text-2xl sm:text-4xl lg:text-5xl font-light text-foreground tracking-tight mb-5">
            {c.cta_heading}
          </h2>
          <p className="font-body text-sm sm:text-lg text-foreground/60 mb-9 max-w-xl mx-auto leading-relaxed">
            {c.cta_body}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/shop">
              <Button
                data-testid="about-cta-shop-btn"
                variant="ctaSecondary"
                className="w-full sm:w-auto rounded-lg px-8 sm:px-10 py-5 sm:py-6 font-body font-semibold text-sm transition-all duration-300 hover:-translate-y-1 shadow-lg"
              >
                Start Shopping <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            {!user && (
              <Link to="/auth">
                <Button
                  data-testid="about-cta-join-btn"
                  variant="ctaPrimary"
                  className="w-full sm:w-auto rounded-lg px-8 sm:px-10 py-5 sm:py-6 font-body font-bold text-sm transition-all duration-300 hover:-translate-y-1 shadow-lg"
                >
                  <PackageCheck className="mr-2 h-5 w-5" /> Create Your Account
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
